<template>
  <page-component :loading="loading">
    <template #content>
      <div v-if="errorMsg" class="text-center mt-6 purple--text text-h6">
        {{ errorMsg }}
      </div>
      <div
        v-else-if="!loading && items.length === 0 && emptyStateHint"
        class="text-center mt-6 grey--text text--darken-2 text-body-1 px-4"
      >
        {{ emptyStateHint }}
      </div>
      <div
        v-else-if="!loading && items.length === 0"
        class="text-center mt-6 grey--text text--darken-1"
      >
        لا توجد طلبات مطابقة لهذا الرقم
      </div>
      <v-row v-else class="mx-0 gy-grid">
        <v-col v-for="item in items" :key="item.id" cols="12" md="6" lg="4">
          <v-hover v-slot="{ hover }">
            <v-sheet
              class="phone-search-sheet rounded-lg overflow-hidden"
              :elevation="hover && cardCanOpen(item) ? 4 : 1"
              :class="sheetClass(item)"
              tabindex="0"
              role="button"
              @click="onCardClick(item)"
              @keydown.enter="onCardClick(item)"
            >
              <div class="phone-search-sheet__toolbar pa-3 d-flex justify-space-between align-start flex-wrap">
                <span
                  class="card-pill permission-badge white--text flex-shrink-0"
                  :class="permissionBadgeClass(item)"
                  :aria-label="permissionLabel(item)"
                >
                  <v-icon x-small color="white" class="permission-badge__icon">{{ permissionIcon(item) }}</v-icon>
                  {{ permissionLabel(item) }}
                </span>
                <span
                  v-if="fileStatusLine(item)"
                  class="card-pill card-pill--status flex-shrink-0 mr-2"
                >
                  {{ truncateText(fileStatusLine(item), 40) }}
                </span>
              </div>

              <div
                class="px-4 pt-2 d-flex justify-space-between align-start flex-wrap"
                style="gap: 12px"
              >
                <div class="text-h6 font-weight-bold primary--text" style="min-width: 0; flex: 1">
                  {{ item.customer && item.customer.name }}
                </div>
                <span class="card-pill card-pill--type flex-shrink-0">{{ item.type || "—" }}</span>
              </div>

              <v-divider class="mx-4 mt-2" />

              <div
                v-if="cardNotePreview(item)"
                class="phone-search-sheet__note px-4 pt-3 pb-0"
              >
                <div class="phone-search-sheet__note-label caption grey--text text--darken-1 mb-1">
                  {{ cardNotePreviewLabel(item) }}
                </div>
                <div class="phone-search-sheet__note-body body-2 grey--text text--darken-3">
                  {{ cardNotePreview(item) }}
                </div>
              </div>

              <v-card-text class="pb-3 pt-3">
                <div class="d-flex flex-wrap mb-3 chips-row">
                  <span
                    v-if="customerMobile(item)"
                    class="card-pill card-pill--phone me-2 mb-1"
                  >
                    <v-icon x-small class="card-pill__icon">mdi-phone</v-icon>
                    {{ customerMobile(item) }}
                  </span>
                  <span v-if="item.req_product_type" class="card-pill card-pill--product me-2 mb-1">
                    {{ productTypeLabelAr(item.req_product_type) }}
                  </span>
                  <span
                    v-if="item.class_id_cs_text"
                    class="card-pill card-pill--cs mb-1"
                  >
                    {{ item.class_id_cs_text }}
                  </span>
                </div>

                <request-lifecycle-banner
                  v-if="lifecycleBannerVisible(item)"
                  class="mt-1 mb-0"
                  :summary-ar="lifecycleSummaryAr(item)"
                  :can-edit="cardCanEdit(item)"
                  :phase="String(item.request_workflow_phase || item.requestWorkflowPhase || '')"
                  :blocked-detail="!cardCanOpen(item)"
                />
              </v-card-text>

              <div
                v-if="cardCanOpen(item)"
                class="phone-search-sheet__cta px-3 py-2 caption grey--text text--darken-1"
              >
                <v-icon x-small color="grey" class="ml-1">mdi-chevron-left</v-icon>
                انقر لفتح الطلب
              </div>
            </v-sheet>
          </v-hover>
        </v-col>
      </v-row>
    </template>
  </page-component>
</template>

<script>
import Axios from "axios";
import { PageComponentMixin } from "@/mixins/page";
import RequestLifecycleBanner from "@/components/common/RequestLifecycleBanner.vue";

const REQ_PRODUCT_TYPE_AR_FALLBACK = {
  commercial_finance: "تمويل تجاري",
  personal_loan: "قرض شخصي",
  buy_property: "شراء عقار",
  self_build: "بناء ذاتي",
  complete_construction: "إكمال البناء",
  land_loan: "أرض وقرض",
  mortgage: "رهن عقار",
  direct_mortgage: "رهن مباشر",
  شراء: "شراء",
  رهن: "رهن",
  شخصي: "شخصي",
  تجاري: "تجاري",
};

export default {
  name: "RequestGlobalSearchByPhone",
  components: { RequestLifecycleBanner },
  mixins: [PageComponentMixin],
  data() {
    return {
      loading: true,
      items: [],
      errorMsg: null,
      emptyStateHint: null,
    };
  },
  watch: {
    "$route.query.phoneNum": {
      immediate: true,
      handler() {
        this.fetchResults();
      },
    },
  },
  methods: {
    truncateText(s, max) {
      const t = (s || "").trim();
      if (t.length <= max) return t;
      return t.slice(0, max).trimEnd() + "…";
    },
    onCardClick(item) {
      if (!item || !this.cardCanOpen(item)) return;
      this.openRequest(item.id);
    },
    sheetClass(item) {
      if (!this.cardCanOpen(item)) return "phone-search-sheet--locked";
      if (!this.cardCanEdit(item)) return "phone-search-sheet--readonly";
      return "phone-search-sheet--editable";
    },
    cardCanOpen(item) {
      if (!item) return false;
      const v = item.can_open_request;
      if (v === false || v === 0 || v === "0") return false;
      return true;
    },
    permissionBadgeClass(item) {
      if (!this.cardCanOpen(item)) return "permission-badge--locked";
      if (this.cardCanEdit(item)) return "permission-badge--editable";
      return "permission-badge--readonly";
    },
    permissionIcon(item) {
      if (!this.cardCanOpen(item)) return "mdi-lock-outline";
      if (this.cardCanEdit(item)) return "mdi-pencil-outline";
      return "mdi-eye-outline";
    },
    permissionLabel(item) {
      if (!this.cardCanOpen(item)) return "بدون فتح التفاصيل";
      if (this.cardCanEdit(item)) return "صلاحية تعديل";
      return "عرض فقط";
    },
    lifecycleBannerVisible(item) {
      const sum = this.lifecycleSummaryAr(item);
      if (sum !== "") return true;
      const ph = item && item.request_workflow_phase;
      if (ph && String(ph) !== "" && String(ph) !== "unknown") return true;
      if (item && (item.can_edit_request === false || item.can_edit_request === 0)) return true;
      if (!this.cardCanOpen(item)) return true;
      return false;
    },
    productTypeLabelAr(raw) {
      if (raw == null || raw === "") {
        return "";
      }
      const key = String(raw).trim();
      const list = this.$store.state.selected_products;
      if (Array.isArray(list) && list.length) {
        const hit = list.find((p) => p.id === key);
        if (hit && hit.value) {
          return hit.value;
        }
      }
      return REQ_PRODUCT_TYPE_AR_FALLBACK[key] || key;
    },
    lifecycleSummaryAr(item) {
      if (!item) return "";
      const s = item.request_status_summary_ar ?? item.requestStatusSummaryAr ?? "";
      return String(s).trim();
    },
    cardCanEdit(item) {
      if (!item) return false;
      const v = item.can_edit_request;
      return v === true || v === 1;
    },
    fileStatusLine(item) {
      return (
        (item && item.request_file_status_ar) ||
        (item && item.status) ||
        ""
      );
    },
    customerMobile(item) {
      const c = item && item.customer;
      if (!c) return "";
      return c.mobile || c.phone || "";
    },
    requestWorkflowPhase(item) {
      if (!item) return "";
      const fromLife =
        item.request_workflow_phase ?? item.requestWorkflowPhase ?? "";
      const fromModel = item.customer_services_status ?? "";
      return String(fromLife || fromModel).trim();
    },
    cardNotePreview(item) {
      const phase = this.requestWorkflowPhase(item);
      let raw = "";
      if (phase === "agent_side") {
        raw = item.comment ?? item.agent_note ?? item.agent_notes ?? "";
      } else if (phase === "cs_side") {
        raw = item.customer_service_note ?? item.cs_note ?? "";
      } else {
        return "";
      }
      return this.truncateText(String(raw).replace(/\r\n/g, "\n"), 160);
    },
    cardNotePreviewLabel(item) {
      const phase = this.requestWorkflowPhase(item);
      if (phase === "agent_side") return "ملاحظة الاستشاري";
      if (phase === "cs_side") return "ملاحظة خدمة العملاء";
      return "";
    },
    openRequest(id) {
      const isCs =
        localStorage.getItem("role") === "0" &&
        localStorage.getItem("is_customer_service") === "1";
      this.$router.push({
        name: isCs ? "customer_service_request_details" : "request_details",
        params: { id: String(id) },
      });
    },
    fetchResults() {
      const phone = this.$route.query.phoneNum;
      if (!phone) {
        this.loading = false;
        this.items = [];
        this.emptyStateHint = null;
        this.errorMsg = "لم يُمرَّر رقم جوال في الرابط.";
        return;
      }
      this.loading = true;
      this.errorMsg = null;
      this.emptyStateHint = null;
      const payload = { page: 1, customer_phone: String(phone) };
      const url = `${this.$store.state.url}/v2/employee/Requests/search-with-lifecycle?type=all`;
      Axios.post(url, payload, { headers: this.request_headers() })
        .then((res) => {
          const body = res.data.payload;
          const hint =
            body && body.phone_search_hint_ar != null
              ? String(body.phone_search_hint_ar).trim()
              : "";
          this.emptyStateHint = hint || null;
          const block = body && body.data ? body.data : null;
          const rows = Array.isArray(block)
            ? block
            : block && Array.isArray(block.data)
              ? block.data
              : [];
          this.items = rows.map((row) => {
            if (!row || typeof row !== "object") {
              return row;
            }
            const sum =
              row.request_status_summary_ar ?? row.requestStatusSummaryAr ?? "";
            return {
              ...row,
              request_status_summary_ar: sum,
              can_open_request:
                row.can_open_request !== undefined && row.can_open_request !== null
                  ? row.can_open_request
                  : true,
            };
          });
        })
        .catch((err) => {
          this.errorMsg =
            (err.response &&
              err.response.data &&
              (err.response.data.message || err.response.data.error)) ||
            "تعذر تنفيذ البحث";
          this.items = [];
          this.emptyStateHint = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.gy-grid {
  margin-top: 4px;
}

.phone-search-sheet {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(0, 0, 0, 0.08);
  outline: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease,
    opacity 0.18s ease;
}

.phone-search-sheet--editable {
  cursor: pointer;
}

.phone-search-sheet--readonly {
  cursor: pointer;
  border-color: rgba(211, 47, 47, 0.28);
}

.phone-search-sheet--locked {
  cursor: not-allowed;
  opacity: 0.93;
}

.theme--dark .phone-search-sheet {
  border-color: rgba(255, 255, 255, 0.12);
}

.phone-search-sheet--editable:hover,
.phone-search-sheet--readonly:hover {
  transform: translateY(-2px);
}

.phone-search-sheet__toolbar {
  background: linear-gradient(
    135deg,
    rgba(63, 14, 64, 0.09),
    rgba(63, 14, 64, 0.02)
  );
}

.theme--dark .phone-search-sheet__toolbar {
  background: rgba(255, 255, 255, 0.05);
}

.permission-badge {
  color: #fff !important;
}

.permission-badge__icon {
  opacity: 0.95;
}

.card-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  border-radius: 999px;
  border: 1px solid transparent;
  box-sizing: border-box;
  max-width: 100%;
}

.card-pill__icon {
  flex-shrink: 0;
  opacity: 0.9;
}

.card-pill--status {
  color: #424242 !important;
  background: rgba(66, 66, 66, 0.06);
  border-color: rgba(66, 66, 66, 0.35);
  white-space: normal;
  text-align: start;
  line-height: 1.25;
  max-width: min(260px, 58%);
}

.card-pill--type {
  color: var(--v-primary-base, #451646) !important;
  background: rgba(69, 22, 70, 0.08);
  border-color: rgba(69, 22, 70, 0.45);
}

.card-pill--phone {
  color: #616161 !important;
  background: rgba(97, 97, 97, 0.06);
  border-color: rgba(97, 97, 97, 0.4);
}

.card-pill--phone .card-pill__icon {
  color: #616161 !important;
}

.card-pill--product {
  color: #5f6368 !important;
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.18);
}

.theme--dark .card-pill--product {
  color: rgba(255, 255, 255, 0.85) !important;
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.22);
}

.card-pill--cs {
  color: #4527a0 !important;
  background: rgba(69, 39, 160, 0.08);
  border-color: rgba(69, 39, 160, 0.45);
}

.theme--dark .card-pill--status {
  color: rgba(255, 255, 255, 0.88) !important;
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.28);
}

.theme--dark .card-pill--type {
  color: #ce93d8 !important;
  background: rgba(206, 147, 216, 0.12);
  border-color: rgba(206, 147, 216, 0.45);
}

.theme--dark .card-pill--phone {
  color: rgba(255, 255, 255, 0.8) !important;
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.22);
}

.theme--dark .card-pill--phone .card-pill__icon {
  color: rgba(255, 255, 255, 0.8) !important;
}

.theme--dark .card-pill--cs {
  color: #b39ddb !important;
  background: rgba(179, 157, 219, 0.12);
  border-color: rgba(179, 157, 219, 0.45);
}

.permission-badge--editable {
  background: #2e7d32;
  border-color: rgba(0, 0, 0, 0.08);
}

.permission-badge--readonly {
  background: #c62828;
  border-color: rgba(0, 0, 0, 0.08);
}

.permission-badge--locked {
  background: #616161;
  border-color: rgba(0, 0, 0, 0.12);
}

.theme--dark .permission-badge--editable {
  background: #43a047;
}

.theme--dark .permission-badge--readonly {
  background: #e53935;
}

.theme--dark .permission-badge--locked {
  background: #757575;
}

.phone-search-sheet__note-body {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
  max-height: 4.35em;
  overflow: hidden;
}

.phone-search-sheet__cta {
  border-top: thin solid rgba(0, 0, 0, 0.06);
  text-align: center;
}

.theme--dark .phone-search-sheet__cta {
  border-top-color: rgba(255, 255, 255, 0.08);
}

.chips-row {
  gap: 0;
}
</style>
